import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SavedSearchResponse } from '@doda/api-client/aspida/@types';
import { FetchBaseStateType } from '@doda/common/src/interfaces/fetchBaseState';
import { LOADING_STATUS } from '@doda/common/src/constants/loadingStatus';
import {
  getAllSavedSearches,
  getSavedSearch,
} from '@doda/common/src/services/savedSearchesAPI';
import { RootState } from '../../store';

/**
 * 初期型
 */
export interface SavedSearchesState {
  savedSearches: FetchBaseStateType<SavedSearchResponse[]>;
  savedSearch: FetchBaseStateType<SavedSearchResponse>;
  mpsc_dtno: string;
  error: {
    message: string;
    redirect: boolean;
  };
}

/**
 * State型
 */
export interface SavedSearchesSliceState {
  savedSearches: ReturnType<typeof savedSearchesReducer>;
}

export const savedSearchesInitialState: SavedSearchesState = {
  savedSearches: {
    loading: LOADING_STATUS.IDLE,
    entity: [],
  },
  savedSearch: {
    loading: LOADING_STATUS.IDLE,
    entity: {
      no: 0,
      name: '',
      savedDate: '',
    },
  },
  mpsc_dtno: '',
  error: {
    message: '',
    redirect: false,
  },
};

/**
 * @remarks 保存した検索条件
 */
export const savedSearchesSlice = createSlice({
  name: 'savedSearches',
  initialState: savedSearchesInitialState,
  reducers: {
    setMpscDtno: (state, action: PayloadAction<string>) => {
      state.mpsc_dtno = action.payload;
    },
    setSavedSearches: (state, action: PayloadAction<SavedSearchResponse>) => {
      state.savedSearches.entity = [action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllSavedSearches.pending, (state) => {
      state.savedSearches.loading = LOADING_STATUS.PENDING;
    });
    builder.addCase(getAllSavedSearches.fulfilled, (state, action) => {
      state.savedSearches.loading = LOADING_STATUS.SUCCESS;
      state.savedSearches.entity = action.payload;
    });
    builder.addCase(getAllSavedSearches.rejected, (state, action) => {
      if (action.payload) state.error = action.payload;
      state.savedSearches.loading = LOADING_STATUS.ERROR;
    });
    builder.addCase(getSavedSearch.pending, (state) => {
      state.savedSearch.loading = LOADING_STATUS.PENDING;
    });
    builder.addCase(getSavedSearch.fulfilled, (state, action) => {
      state.savedSearch.loading = LOADING_STATUS.SUCCESS;
      state.savedSearch.entity = action.payload;
    });
    builder.addCase(getSavedSearch.rejected, (state, action) => {
      if (action.payload) state.error = action.payload;
      state.savedSearch.loading = LOADING_STATUS.ERROR;
    });
  },
});

// Create Action
export const { setMpscDtno, setSavedSearches } = savedSearchesSlice.actions;

// reducer
export const savedSearchesReducer = savedSearchesSlice.reducer;

// selector
/**
 * savedSearchesAllSelector
 * @param state
 * @returns SavedSearchResponse[]
 */
export const savedSearchesAllSelector = (state: RootState) =>
  state.savedSearches.savedSearches.entity;

/**
 * savedSearchesSelector
 * @param state
 * @returns SavedSearchResponse
 */
export const savedSearchesSelector = (state: RootState) =>
  state.savedSearches.savedSearches.entity.find(
    (x) => x.no === Number(state.savedSearches.mpsc_dtno)
  );

/**
 * mpscDtnoSelector
 * @param state
 * @returns mpsc_dtno
 */
export const mpscDtnoSelector = (state: RootState) =>
  state.savedSearches.mpsc_dtno;

/**
 * savedSearchSelector
 * @param state
 * @returns SavedSearchResponse
 */
export const savedSearchSelector = (state) =>
  state.savedSearches?.savedSearch.entity; // スライスを使用していない画面でも呼び出されるため

/**
 * savedSearchesErrorSelector
 * @param state
 * @returns error
 */
export const savedSearchesErrorSelector = (state) => state.savedSearches.error;
